import React, { useState, useRef } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import SaveIcon from "@mui/icons-material/Save";
import { Box, IconButton, Modal, Slider, Stack, Typography } from "@mui/material";
import { Refresh as Refresh, Close as CloseIcon } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { English } from "../../../constant/EnglishFallback";
import { BIBlue } from "../../../assets/helpers";

function RatioCropModal({ open, onClose, selectedImageUrl, setPictureList }) {
  const [crop, setCrop] = useState({ aspect: 1 });
  const [combinedImageUrl, setCombinedImageUrl] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [showRotationSlider, setShowRotationSlider] = useState(false);
  const imageRef = useRef(null);

  const { multiLanguageData } = useSelector((state) => state?.main);
  const LanguageData = multiLanguageData?.Montages || {};
  const {
    TEXT_ADJUST_THE_CROP_AREA = English.Montages.TEXT_ADJUST_THE_CROP_AREA,
    TEXT_PREVIEW = English.Montages.TEXT_PREVIEW,
    TEXT_ROTATE_IMAGE = English.Montages.TEXT_ROTATE_IMAGE
  } = LanguageData || {};

  const applyCropAndRotation = (crop, rotation) => {
    if (imageRef.current && crop.width && crop.height) {
      const canvas = document.createElement("canvas");
      const scaleX = imageRef.current.naturalWidth / imageRef.current.width;
      const scaleY = imageRef.current.naturalHeight / imageRef.current.height;

      if (rotation % 180 !== 0) {
        canvas.width = crop.height;
        canvas.height = crop.width;
      } else {
        canvas.width = crop.width;
        canvas.height = crop.height;
      }

      const ctx = canvas.getContext("2d");
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.drawImage(
        imageRef.current,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        -crop.width / 2,
        -crop.height / 2,
        crop.width,
        crop.height
      );

      const combinedUrl = canvas.toDataURL("image/jpeg");
      setCombinedImageUrl(combinedUrl);
    }
  };

  const applyRotationAndPreview = (rotation) => {
    if (imageRef.current) {
      const canvas = document.createElement("canvas");
      const width = imageRef.current.naturalWidth;
      const height = imageRef.current.naturalHeight;

      if (rotation % 180 !== 0) {
        canvas.width = height;
        canvas.height = width;
      } else {
        canvas.width = width;
        canvas.height = height;
      }

      const ctx = canvas.getContext("2d");
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.drawImage(imageRef.current, -width / 2, -height / 2, width, height);

      const rotatedUrl = canvas.toDataURL("image/jpeg");
      setCombinedImageUrl(rotatedUrl);
    }
  };

  const handleRotate = (event, newValue) => {
    setRotation(newValue);
    applyRotationAndPreview(newValue);
  };

  const handleClose = () => {
    setCrop({ aspect: 1 });
    setRotation(0);
    setCombinedImageUrl(null);
    setShowRotationSlider(false);
    onClose();
  };

  const handleSave = () => {
    if (combinedImageUrl) {
      const blob = dataURLtoBlob(combinedImageUrl);
      const file = new File([blob], "rotated-cropped-image.jpg", {
        type: "image/jpeg"
      });

      setPictureList((prevList) => {
        const updatedList = prevList.filter((picture) => picture.id !== selectedImageUrl.id);
        const updatedImage = {
          ...selectedImageUrl,
          fileUrl: combinedImageUrl,
          galleryUploadUrl: file,
          imageFrom: "Gallery",
          deleteCropPicID: selectedImageUrl?.id
        };
        updatedList.push(updatedImage);
        return updatedList;
      });

      setTimeout(() => {
        setCrop({ aspect: 1 });
        setRotation(0);
        setCombinedImageUrl(null);
        setShowRotationSlider(false);
        onClose();
      }, 100);
    }
  };

  const dataURLtoBlob = (dataURL) => {
    const arr = dataURL.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new Blob([u8arr], { type: mime });
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Box
          bgcolor="white"
          borderRadius="8px"
          boxShadow={3}
          width="500px"
          className="user-modal-responsive"
          height="auto"
          p={2}>
          <Box sx={{ position:'sticky', top:'-10px', zIndex:1, background:'white' }} >
            <Stack flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'}>
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>

              <Typography variant="body2" color="textSecondary" align="center">
                {TEXT_ADJUST_THE_CROP_AREA}
              </Typography>

              <Stack flexDirection={'row'}>
                <IconButton
                  onClick={() => setShowRotationSlider(!showRotationSlider)}
                  style={{ color: BIBlue }}>
                  <Refresh />
                </IconButton>

                <IconButton onClick={handleSave} style={{ color: BIBlue }}>
                  <SaveIcon />
                </IconButton>
              </Stack>
            </Stack>
          </Box>

          {selectedImageUrl?.fileUrl && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              style={{ height: "350px", overflow: "hidden" }}>
              <ReactCrop
                crop={crop}
                onImageLoaded={(image) => {
                  imageRef.current = image;
                  applyRotationAndPreview(rotation);
                }}
                onComplete={(crop) => applyCropAndRotation(crop, rotation)}
                onChange={(newCrop) => setCrop(newCrop)}
                aspect={1}>
                <img
                  src={selectedImageUrl.fileUrl}
                  alt="Crop Me"
                  ref={imageRef}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "350px",
                    objectFit: "contain"
                  }}
                  crossOrigin="anonymous"
                />
              </ReactCrop>
            </Box>
          )}

          {combinedImageUrl && (
            <Box mt={2} textAlign="center">
              <Typography variant="body1">{TEXT_PREVIEW} :</Typography>
              <img
                src={combinedImageUrl}
                alt="Cropped and Rotated Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "200px",
                  borderRadius: "8px"
                }}
              />
            </Box>
          )}

          {showRotationSlider && (
            <Box mt={2} textAlign="center">
              <Typography variant="body1">{TEXT_ROTATE_IMAGE}:</Typography>
              <Slider
                value={rotation}
                onChange={handleRotate}
                aria-labelledby="rotation-slider"
                min={0}
                max={360}
                step={1}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
}

export default RatioCropModal;
